import React, { useEffect } from 'react';
import { Box, Typography, Checkbox, Stack, colors } from '@mui/material';
import parse from 'html-react-parser';
import { Section, SubSection } from 'views/App/Portal/Policy/_types/policy';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';

interface SectionProps {
  section?: Section;
  allSections?: Section[];
  isAssetSelected: (id: string) => boolean;
  onToggleAsset: (id: string, label: string) => void;
  onAssetClick: (section: Section) => void;
  activeSectionId: string | null;
  showAll?: boolean;
}

export const DTBSections: React.FC<SectionProps> = ({
  section,
  allSections = [],
  isAssetSelected,
  onToggleAsset,
  onAssetClick,
  activeSectionId,
  showAll = false,
}) => {
  useEffect(() => {
    if (activeSectionId) {
      const el = document.getElementById(`section_${activeSectionId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [activeSectionId]);

  const renderSection = (section: Section) => {
    const isChecked = isAssetSelected(section.id);
    const backgroundColor = section.id === activeSectionId ? colors.amber[50] : 'transparent';
    return (
      <Stack
        spacing={2}
        id={`section_${section.id}`}
        sx={{ backgroundColor }}
        key={section.id}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => onAssetClick(section)}
        >
          <Checkbox
            checked={isChecked}
            onClick={(e) => {
              e.stopPropagation();
              onToggleAsset(section.id, [section.key, section.title].filter(Boolean).join(': '));
            }}
            sx={{ mr: 1 }}
          />
          <Typography variant='h5'>{[section.key, section.title].filter(Boolean).join(': ')}</Typography>
        </Box>
        {section.content && (
          <Typography
            variant='body1'
            sx={{ pl: 4 }}
          >
            {parse(section.content || '', {
              replace(node: any, ind: number) {
                return PolicyContentHelper.handleContent(node, ind);
              },
            })}
          </Typography>
        )}
        {section.subSections?.map((sub) => (
          <DTBSubSection
            key={sub.id}
            section={sub}
            isAssetSelected={isAssetSelected}
            onToggleAsset={onToggleAsset}
            onAssetClick={onAssetClick}
            activeSectionId={activeSectionId}
          />
        ))}
      </Stack>
    );
  };

  if (showAll && allSections.length > 0) {
    return <>{allSections.map(renderSection)}</>;
  }
  if (section) {
    return renderSection(section);
  }
  return null;
};

interface SubSectionProps {
  section: SubSection;
  isAssetSelected: (id: string) => boolean;
  onToggleAsset: (id: string, label: string) => void;
  onAssetClick: (section: any) => void;
  activeSectionId: string | null;
}

const DTBSubSection: React.FC<SubSectionProps> = ({
  section,
  isAssetSelected,
  onToggleAsset,
  onAssetClick,
  activeSectionId,
}) => {
  const isChecked = isAssetSelected(section.id);
  const backgroundColor = section.id === activeSectionId ? colors.amber[50] : 'transparent';

  return (
    <Stack
      spacing={2}
      sx={{ ml: 4, borderLeft: '2px solid #eee', pl: 2, backgroundColor }}
      id={`section_${section.id}`}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        onClick={() => onAssetClick(section)}
      >
        <Checkbox
          checked={isChecked}
          onClick={(e) => {
            e.stopPropagation();
            onToggleAsset(section.id, [section.key, section.title].filter(Boolean).join(': '));
          }}
          sx={{ mr: 1 }}
        />
        <Typography variant='h6'>{[section.key, section.title].filter(Boolean).join(': ')}</Typography>
      </Box>
      {section.content && (
        <Typography
          variant='body1'
          sx={{ pl: 4 }}
        >
          {parse(section.content || '', {
            replace(node: any, ind: number) {
              return PolicyContentHelper.handleContent(node, ind);
            },
          })}
        </Typography>
      )}
    </Stack>
  );
};

export default DTBSections;
